import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { EXPERIENCE_AVAILABLE_DAY_LIMIT } from 'app/typings/core/admin/experiences/enum/experienceAvailableDayLimit';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import { ExtranetExperienceStatus } from 'app/typings/core/extranet/hotel/experiences';
import type {
  ExtranetHotelPack,
  ExtranetHotelPackExperience,
} from 'app/typings/core/extranet/hotel/packs';
import type {
  InventoryPack,
  InventoryResponse,
} from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import {
  addDays,
  eachDayOfInterval,
  isAfter,
  isBefore,
  startOfDay,
} from 'app/utils/dayjs';
import { isPackVisibleInExtranet } from 'app/utils/extranet';
import { applyDiscountRate, multiplyPrice } from 'app/utils/pricing';

import type { ExperienceOpening } from './experiences';
import { computeExperiences, getExperiencePrice } from './experiences';
import type { RoomOpeningInventory } from './rooms';
import { computeRooms } from './rooms';

type ExperienceDetails = {
  maxAllowedBookingCount?: number;
  experiencePrice: number;
  discountedExperiencePrice: number;
  isIncluded: boolean;
  isOptional: boolean;
  experienceId: number;
};

export type AvailableExperience = {
  experienceId: number;
  discountedPrice: number;
};

type ExperiencesDetails = {
  maxAllowedBookingCount?: number;
  totalExperiencePrice: number;
  discountedTotalExperiencePrice: number;
  blockingExperiences: number[];
  availableExperiences: AvailableExperience[];
};

export type PackRoomDetails = {
  roomId: number;
  roomStock?: number;
  totalPrice?: number;
  discountedTotalPrice?: number;
  discount?: number;
  sellableStock: number;
  inventoryStatus: RoomOpeningInventory['inventoryStatus'];
};

export enum PackType {
  WITH_ROOM = 'withRoom',
  WITHOUT_ROOM = 'withoutRoom',
}

export type WithoutRoomPackDetails = {
  totalPrice: number;
  discountedTotalPrice: number;
  discount: number;
};

type PackOpeningStatus = 'PUBLISHED' | 'UNPUBLISHED' | 'CLOSED';

type PackOpeningMessage =
  | 'PUBLISHED'
  | 'MISSING_ROOM_STOCK'
  | 'MISSING_EXPERIENCES_STOCK'
  | 'MAX_BOOKING_REACHED'
  | 'PAST_SALES'
  | 'FUTURE_SALES';

type PackOpeningStatusAndMessage = {
  inventoryStatus: PackOpeningStatus;
  message: PackOpeningMessage;
  messageReferencedIds?: number[];
};

export type PackDetails = PackOpeningStatusAndMessage & {
  maxBooking?: number;
  booked: number;
  allowedBookingCount?: number;
  stockOnSale?: number;
  sellableCategoryCount: number;
  totalCategoryCount: number;
  packScore?: number;
  availableExperiences: AvailableExperience[];
} & (
    | {
        type: PackType.WITH_ROOM;
        roomsDetails: PackRoomDetails[];
        priceFrom?: number;
        priceFromDiscount?: number;
        lowestRoomId?: number;
        lowestRoomPrice?: number;
      }
    | {
        type: PackType.WITHOUT_ROOM;
        priceDetails: WithoutRoomPackDetails;
      }
  );

export type ComputePacksResult = Record<
  string,
  {
    packs: Record<number, PackDetails>;
    rooms: Record<number, RoomOpeningInventory>;
    experiences: Record<number, ExperienceOpening>;
  }
>;

// Constants
export const DEFAULT_PAX = 2;

// Computes minimum price for night stay experiences (check-in or check-out)
const computeMinimumExperiencePrice = (
  canBookCheckin: boolean,
  canBookCheckout: boolean,
  checkinPrice: number,
  checkoutPrice: number
): number => {
  // If both or neither are available, return the minimum price
  if (canBookCheckin === canBookCheckout) {
    return Math.min(checkinPrice, checkoutPrice);
  }

  // Return the price for the available option
  return canBookCheckin ? checkinPrice : checkoutPrice;
};

// Calculates stock units needed per booking based on per-person or per-booking configuration
export const calculateStockNecessaryPerBooking = (
  packExperience: ExtranetHotelPackExperience
): number => {
  return packExperience.isPerPerson
    ? packExperience.quantity * DEFAULT_PAX
    : packExperience.quantity;
};

const applyPackDiscount = (
  unitPrice: number,
  quantity: number,
  packExperience: ExtranetHotelPackExperience
): number =>
  multiplyPrice(
    applyDiscountRate(unitPrice, packExperience.discount),
    quantity
  );

// Gets experience details for daytime packs (single date only)
const getExperienceDetailsForDaytimeDate = (
  date: Date,
  experience: ExtranetHotelExperience,
  packExperience: ExtranetHotelPackExperience,
  experienceOpenings: Record<string, Record<number, ExperienceOpening>>
): Omit<ExperienceDetails, 'isIncluded' | 'isOptional' | 'experienceId'> => {
  const dateString = formatDateWithoutHour(date);
  const experienceOpening = experienceOpenings[dateString]?.[experience.id];

  const publishedStock = experienceOpening
    ? experienceOpening.publishedStock
    : 0;

  const price = getExperiencePrice(experienceOpening, true);
  const stockNecessaryPerBooking =
    calculateStockNecessaryPerBooking(packExperience);

  // Calculate max bookings based on available stock
  const maxAllowedBookingCount =
    publishedStock === undefined || publishedStock === null
      ? undefined
      : Math.floor(publishedStock / stockNecessaryPerBooking);

  const basePrice = price * stockNecessaryPerBooking;

  return {
    maxAllowedBookingCount,
    experiencePrice: basePrice,
    discountedExperiencePrice: applyPackDiscount(
      price,
      stockNecessaryPerBooking,
      packExperience
    ),
  };
};

// Gets the stock on sale for a specific day limit (CHECK_IN or CHECK_OUT) if available
const getPublishedStockForDayLimit = (
  experience: ExtranetHotelExperience,
  experienceOpening: ExperienceOpening | undefined,
  dayLimit: EXPERIENCE_AVAILABLE_DAY_LIMIT
): number | undefined | null => {
  const isAvailableForDayLimit =
    experience.availableDayLimits?.includes(dayLimit);

  if (isAvailableForDayLimit && experienceOpening !== undefined) {
    return experienceOpening.publishedStock;
  }

  return 0;
};

// Calculates max booking count from remaining stock
const calculateMaxBookingCount = (
  remainingStock: number | undefined | null,
  stockNecessaryPerBooking: number
): number | undefined => {
  return remainingStock === undefined || remainingStock === null
    ? undefined
    : Math.floor(remainingStock / stockNecessaryPerBooking);
};

// Gets experience details for night stay packs (needs both check-in and check-out dates)
const getExperienceDetailsForNightStayDate = (
  date: Date,
  experience: ExtranetHotelExperience,
  packExperience: ExtranetHotelPackExperience,
  experienceOpenings: Record<string, Record<number, ExperienceOpening>>
): Omit<ExperienceDetails, 'isIncluded' | 'isOptional' | 'experienceId'> => {
  const checkinString = formatDateWithoutHour(date);
  const checkoutString = formatDateWithoutHour(addDays(date, 1));

  const checkinExperienceOpening =
    experienceOpenings[checkinString]?.[experience.id];
  const checkoutExperienceOpening =
    experienceOpenings[checkoutString]?.[experience.id];

  // Get stock for both check-in and check-out day limits
  const checkinPublishedStock = getPublishedStockForDayLimit(
    experience,
    checkinExperienceOpening,
    EXPERIENCE_AVAILABLE_DAY_LIMIT.CHECK_IN
  );
  const checkoutPublishedStock = getPublishedStockForDayLimit(
    experience,
    checkoutExperienceOpening,
    EXPERIENCE_AVAILABLE_DAY_LIMIT.CHECK_OUT
  );

  const checkinPrice = getExperiencePrice(checkinExperienceOpening, false);
  const checkoutPrice = getExperiencePrice(checkoutExperienceOpening, false);

  const stockNecessaryPerBooking =
    calculateStockNecessaryPerBooking(packExperience);

  const checkinMaxBookingCount = calculateMaxBookingCount(
    checkinPublishedStock,
    stockNecessaryPerBooking
  );
  const checkoutMaxBookingCount = calculateMaxBookingCount(
    checkoutPublishedStock,
    stockNecessaryPerBooking
  );

  // Total max booking is sum of check-in and check-out availability
  const maxAllowedBookingCount =
    checkinMaxBookingCount !== undefined &&
    checkoutMaxBookingCount !== undefined
      ? checkinMaxBookingCount + checkoutMaxBookingCount
      : undefined;

  const canBookCheckin =
    checkinMaxBookingCount === undefined || checkinMaxBookingCount > 0;
  const canBookCheckout =
    checkoutMaxBookingCount === undefined || checkoutMaxBookingCount > 0;

  // Use minimum price between check-in and check-out
  const minPrice = computeMinimumExperiencePrice(
    canBookCheckin,
    canBookCheckout,
    checkinPrice,
    checkoutPrice
  );
  const experiencePrice = minPrice * stockNecessaryPerBooking;

  return {
    maxAllowedBookingCount,
    experiencePrice,
    discountedExperiencePrice: applyPackDiscount(
      minPrice,
      stockNecessaryPerBooking,
      packExperience
    ),
  };
};

// Delegates to daytime or night stay experience calculation based on pack type
const getExperienceDetailsForDate = (
  date: Date,
  isDaytime: boolean,
  experience: ExtranetHotelExperience,
  packExperience: ExtranetHotelPackExperience,
  experienceOpenings: Record<string, Record<number, ExperienceOpening>>
) => {
  if (isDaytime) {
    return getExperienceDetailsForDaytimeDate(
      date,
      experience,
      packExperience,
      experienceOpenings
    );
  }

  return getExperienceDetailsForNightStayDate(
    date,
    experience,
    packExperience,
    experienceOpenings
  );
};

// Calculates max booking count as minimum across all mandatory included experiences
const calculateMaxAllowedBookingCount = (
  experiencesDetails: ExperienceDetails[]
): number | undefined => {
  const mandatoryIncludedExperiences = experiencesDetails.filter(
    (exp) => exp.isIncluded && !exp.isOptional
  );

  if (mandatoryIncludedExperiences.length === 0) {
    return undefined;
  }

  const bookingCounts = mandatoryIncludedExperiences
    .map((exp) => exp.maxAllowedBookingCount)
    .filter((count): count is number => count !== undefined);

  if (bookingCounts.length === 0) {
    return undefined;
  }

  // Return minimum to ensure all mandatory experiences can be booked
  return Math.min(...bookingCounts);
};

// Sums prices for all included experiences that have stock available
const calculateTotalPrices = (
  experiencesDetails: ExperienceDetails[]
): {
  totalExperiencePrice: number;
  discountedTotalExperiencePrice: number;
  availableExperiences: AvailableExperience[];
} => {
  return experiencesDetails.reduce<{
    totalExperiencePrice: number;
    discountedTotalExperiencePrice: number;
    availableExperiences: AvailableExperience[];
  }>(
    (acc, experience) => {
      const isIncluded = experience.isIncluded;
      const hasStock = experience.maxAllowedBookingCount !== 0;

      // Only include experiences that are included and have stock
      if (!isIncluded || (!hasStock && experience.isOptional)) {
        return acc;
      }

      return {
        totalExperiencePrice:
          acc.totalExperiencePrice + experience.experiencePrice,
        discountedTotalExperiencePrice:
          acc.discountedTotalExperiencePrice +
          experience.discountedExperiencePrice,
        availableExperiences: [
          ...acc.availableExperiences,
          {
            experienceId: experience.experienceId,
            discountedPrice: experience.discountedExperiencePrice,
          },
        ],
      };
    },
    {
      totalExperiencePrice: 0,
      discountedTotalExperiencePrice: 0,
      availableExperiences: [],
    }
  );
};

// Aggregates experience details for all experiences in a pack for a date
const getExperiencesForDate = (
  date: Date,
  packData: ExtranetHotelPack,
  experiences: ExtranetHotelExperience[],
  experienceOpenings: Record<string, Record<number, ExperienceOpening>>,
  visibleExperienceIds: Set<number>
): ExperiencesDetails => {
  // Build details for each experience in the pack
  const experiencesDetails: ExperienceDetails[] =
    packData.packExperiences.reduce((acc, packExperience) => {
      const extranetExperience = experiences.find(
        (e) => e.id === packExperience.experienceId
      );

      if (extranetExperience === undefined) {
        return acc;
      }

      const experienceDetails = getExperienceDetailsForDate(
        date,
        packData.isDaytime,
        extranetExperience,
        packExperience,
        experienceOpenings
      );

      acc.push({
        ...experienceDetails,
        isIncluded: packExperience.isIncluded,
        isOptional: packExperience.isOptional,
        experienceId: packExperience.experienceId,
      });

      return acc;
    }, [] as ExperienceDetails[]);

  const maxAllowedBookingCount =
    calculateMaxAllowedBookingCount(experiencesDetails);
  const {
    totalExperiencePrice,
    discountedTotalExperiencePrice,
    availableExperiences,
  } = calculateTotalPrices(experiencesDetails);

  // Find mandatory included experiences with no stock (blocking pack sale)
  // Only include experiences that are visible in inventory
  const blockingExperiences = experiencesDetails
    .filter(
      (experience) =>
        !experience.isOptional &&
        experience.isIncluded &&
        experience.maxAllowedBookingCount === 0 &&
        visibleExperienceIds.has(experience.experienceId)
    )
    .map((experience) => experience.experienceId);

  return {
    maxAllowedBookingCount,
    totalExperiencePrice,
    discountedTotalExperiencePrice,
    blockingExperiences,
    availableExperiences,
  };
};

// Calculates discount percentage as a formatted string
const calculateDiscountPercentage = (
  totalPrice: number,
  discountedTotalPrice: number
): number => {
  if (totalPrice === 0) {
    return 0;
  }

  const discountPercentage =
    ((totalPrice - discountedTotalPrice) / totalPrice) * 100;

  return parseFloat(discountPercentage.toFixed(2));
};

// Calculates sellable stock as minimum of room stock, experience max booking, and pack max booking
const calculateSellableStock = (
  roomStock: number | undefined,
  experienceMaxBooking: number | undefined,
  packMaxBooking: number | undefined
): number => {
  if (roomStock === undefined) {
    return 0;
  }

  const stocks = [roomStock, experienceMaxBooking, packMaxBooking].filter(
    (stock): stock is number => stock !== undefined
  );

  if (stocks.length === 0) {
    return 0;
  }

  // Sellable stock is constrained by the most limiting factor
  return Math.min(...stocks);
};

// Computes room details including prices, stock, and discount for a date
const getRoomDetailsForDate = (
  roomId: number,
  experiencesDetails: ExperiencesDetails,
  roomOpening: RoomOpeningInventory,
  packMaxBooking?: number
): PackRoomDetails => {
  const sellableStock = calculateSellableStock(
    roomOpening.remainingStock,
    experiencesDetails.maxAllowedBookingCount,
    packMaxBooking
  );

  // Total price combines room price with included experience prices
  const totalPrice =
    roomOpening.price !== undefined
      ? (roomOpening.price ?? 0) + experiencesDetails.totalExperiencePrice
      : undefined;
  const discountedTotalPrice =
    roomOpening.price !== undefined
      ? (roomOpening.discountPrice ?? 0) +
        experiencesDetails.discountedTotalExperiencePrice
      : undefined;

  const discount =
    totalPrice !== undefined && discountedTotalPrice !== undefined
      ? calculateDiscountPercentage(totalPrice, discountedTotalPrice)
      : undefined;

  return {
    roomId,
    totalPrice,
    discountedTotalPrice,
    discount,
    sellableStock,
    roomStock: roomOpening.remainingStock,
    inventoryStatus: roomOpening.inventoryStatus,
  };
};

// Determines pack status and message based on sales window, stock, and booking limits
const getPackStatusAndMessage = (
  isPastSales: boolean,
  isFutureSales: boolean,
  roomsDetails: PackRoomDetails[],
  experiencesDetails: ExperiencesDetails,
  isDaytime: boolean,
  allowedBookingCount?: number
): PackOpeningStatusAndMessage => {
  // Check sales window first
  if (isPastSales) {
    return { inventoryStatus: 'CLOSED', message: 'PAST_SALES' };
  }

  if (isFutureSales) {
    return { inventoryStatus: 'CLOSED', message: 'FUTURE_SALES' };
  }

  // Check booking limits
  if (allowedBookingCount !== undefined && allowedBookingCount === 0) {
    return { inventoryStatus: 'UNPUBLISHED', message: 'MAX_BOOKING_REACHED' };
  }

  // Check mandatory experience stock
  if (experiencesDetails.blockingExperiences.length > 0) {
    return {
      inventoryStatus: 'UNPUBLISHED',
      message: 'MISSING_EXPERIENCES_STOCK',
      messageReferencedIds: experiencesDetails.blockingExperiences,
    };
  }

  // A room can keep stock while not being on sale (stop sell, closed on CM, missing
  // price/discount…). The pack is only bookable if at least one room is actually
  // PUBLISHED — checking room stock alone would flag closed rooms as bookable.
  const notOnSaleRooms = roomsDetails
    .filter((room) => room.inventoryStatus !== 'PUBLISHED')
    .map((room) => room.roomId);

  if (!!roomsDetails.length && notOnSaleRooms.length === roomsDetails.length) {
    return {
      inventoryStatus: 'UNPUBLISHED',
      message: 'MISSING_ROOM_STOCK',
      messageReferencedIds: notOnSaleRooms,
    };
  }

  if (!isDaytime && roomsDetails.length === 0) {
    return {
      inventoryStatus: 'UNPUBLISHED',
      message: 'MISSING_ROOM_STOCK',
    };
  }

  return {
    inventoryStatus: 'PUBLISHED',
    message: 'PUBLISHED',
    messageReferencedIds: [],
  };
};

export const getPackScoreOnSingleDate = (discount: number) => {
  const convertDiscount = discount / 100;

  if (convertDiscount < 0.18) {
    return (
      (50 * (1 - Math.exp(-10 * convertDiscount))) / (1 - Math.exp(-10 * 0.18))
    );
  }

  return 50 + 50 * (1 - Math.exp(-10 * (convertDiscount - 0.18)));
};

// Core function: computes complete pack details for a single date
export const computePackOnSingleDate = (
  saleEndDate: Date,
  date: Date,
  packData: ExtranetHotelPack,
  experiences: ExtranetHotelExperience[],
  roomOpenings: Record<number, RoomOpeningInventory>,
  experienceOpenings: Record<string, Record<number, ExperienceOpening>>,
  packInventory?: InventoryPack,
  visibleExperienceIds?: Set<number>
): PackDetails => {
  const isPastSales = isBefore(startOfDay(date), startOfDay(new Date()));
  const isFutureSales = isAfter(
    startOfDay(date),
    startOfDay(new Date(saleEndDate))
  );

  // Calculate remaining bookings if max booking is set
  const allowedBookingCount =
    packInventory?.maxBooking !== undefined
      ? Math.max(0, packInventory.maxBooking - packInventory.bookedPack)
      : undefined;

  const experiencesDetails = getExperiencesForDate(
    date,
    packData,
    experiences,
    experienceOpenings,
    visibleExperienceIds ?? new Set<number>()
  );

  // For daytime packs, return withoutRoom type
  if (packData.isDaytime) {
    // For daytime packs, price is based only on experiences
    const totalPrice = experiencesDetails.totalExperiencePrice;
    const discountedTotalPrice =
      experiencesDetails.discountedTotalExperiencePrice;
    const discount = calculateDiscountPercentage(
      totalPrice,
      discountedTotalPrice
    );

    const stockArray = [
      experiencesDetails.maxAllowedBookingCount,
      allowedBookingCount,
    ].filter((stock): stock is number => stock !== undefined);

    const stockOnSale =
      stockArray.length > 0 ? Math.min(...stockArray) : undefined;

    const statusAndMessage = getPackStatusAndMessage(
      isPastSales,
      isFutureSales,
      [],
      experiencesDetails,
      true,
      allowedBookingCount
    );

    return {
      ...statusAndMessage,
      maxBooking: packInventory?.maxBooking,
      stockOnSale,
      booked: packInventory?.bookedPack ?? 0,
      allowedBookingCount,
      sellableCategoryCount: 0,
      totalCategoryCount: 0,
      availableExperiences: experiencesDetails.availableExperiences,
      type: PackType.WITHOUT_ROOM,
      priceDetails: {
        totalPrice,
        discountedTotalPrice,
        discount,
      },
    };
  }

  const roomsDetails: PackRoomDetails[] = packData.packRooms.reduce(
    (acc, room) => {
      const roomOpening = roomOpenings[room.roomId];

      if (
        roomOpening === undefined ||
        roomOpening.discount === undefined ||
        roomOpening.discount < 0
      ) {
        return acc;
      }

      const roomDetails = getRoomDetailsForDate(
        room.roomId,
        experiencesDetails,
        roomOpenings[room.roomId],
        allowedBookingCount
      );

      acc.push(roomDetails);

      return acc;
    },
    [] as PackRoomDetails[]
  );

  const statusAndMessage = getPackStatusAndMessage(
    isPastSales,
    isFutureSales,
    roomsDetails,
    experiencesDetails,
    false,
    allowedBookingCount
  );

  // Only published rooms are actually on sale: a room can keep stock while being
  // closed (stop sell, closed on CM, closed by Staycation) or unpublished
  // (missing price, discount too low…). Those must not inflate the on-sale stock
  // nor the on-sale category count.
  const onSaleRooms = roomsDetails.filter(
    (room) => room.inventoryStatus === 'PUBLISHED'
  );

  const roomsRemainingStock = onSaleRooms.reduce(
    (acc, room) => acc + (room.roomStock || 0),
    0
  );

  const stockOnSale = calculateSellableStock(
    roomsRemainingStock,
    experiencesDetails.maxAllowedBookingCount,
    allowedBookingCount
  );

  const sellableRoom = roomsDetails.filter((room) => room.sellableStock > 0);

  const onSaleRoom = onSaleRooms.filter((room) => room.sellableStock > 0);

  const roomsToCheck =
    !isPastSales && sellableRoom.length > 0 ? sellableRoom : roomsDetails;
  const validRooms = roomsToCheck.filter(
    (room) => room.discountedTotalPrice !== undefined
  );

  const minRoom =
    validRooms.length > 0
      ? validRooms.reduce((min, room) =>
          (room.discountedTotalPrice ?? Infinity) <
          (min.discountedTotalPrice ?? Infinity)
            ? room
            : min
        )
      : undefined;

  const priceFrom = minRoom?.discountedTotalPrice;
  const priceFromDiscount = minRoom?.discount;
  const lowestRoomId = minRoom?.roomId;
  const lowestRoomPrice =
    lowestRoomId !== undefined
      ? roomOpenings[lowestRoomId]?.discountPrice
      : undefined;

  // For night stay packs, return withRoom type
  return {
    ...statusAndMessage,
    maxBooking: packInventory?.maxBooking,
    stockOnSale,
    booked: packInventory?.bookedPack ?? 0,
    allowedBookingCount,
    sellableCategoryCount: onSaleRoom.length,
    totalCategoryCount: packData.packRooms.length,
    availableExperiences: experiencesDetails.availableExperiences,
    type: PackType.WITH_ROOM,
    roomsDetails: roomsDetails,
    priceFrom,
    priceFromDiscount,
    lowestRoomId,
    lowestRoomPrice,
    packScore:
      priceFrom !== undefined
        ? getPackScoreOnSingleDate(priceFromDiscount ?? 0)
        : undefined,
  };
};

// Filters room openings to only include rooms that belong to the pack
export const getRoomOpeningsForPack = (
  packData: ExtranetHotelPack,
  computedRoomOpenings: Record<number, RoomOpeningInventory>
): RoomOpeningInventory[] => {
  const packRoomIds = new Set(packData.packRooms.map((room) => room.roomId));

  return Object.values(computedRoomOpenings).filter((opening) =>
    packRoomIds.has(opening.roomId)
  );
};

// Computes pack details for all packs on a specific date
const computePacksForDate = (
  saleEndDate: Date,
  date: Date,
  packs: ExtranetHotelPack[],
  inventoryInput: InventoryResponse,
  experiences: ExtranetHotelExperience[],
  computedExperiences: Record<string, Record<number, ExperienceOpening>>,
  computedRoomOpenings: Record<number, RoomOpeningInventory>,
  visibleExperienceIds: Set<number>
): Record<string, PackDetails> => {
  const dateKey = formatDateWithoutHour(date);
  const packOpenings = inventoryInput[dateKey]?.packs ?? {};

  return packs.reduce(
    (result, packData) => {
      const packOpening = packOpenings[packData.id];

      const roomOpeningsForPack = getRoomOpeningsForPack(
        packData,
        computedRoomOpenings
      );

      // Convert array to record for compatibility with computePackOnSingleDate
      const roomOpeningsRecord = roomOpeningsForPack.reduce(
        (acc, opening) => {
          acc[opening.roomId] = opening;

          return acc;
        },
        {} as Record<number, RoomOpeningInventory>
      );

      result[packData.id] = computePackOnSingleDate(
        saleEndDate,
        date,
        packData,
        experiences,
        roomOpeningsRecord,
        computedExperiences,
        packOpening,
        visibleExperienceIds
      );

      return result;
    },
    {} as Record<string, PackDetails>
  );
};

// Base filter for experiences: hotel-level, published, allow ADULT pax type
const baseFilterHotelExperiences = (
  experiences: ExtranetHotelExperience[]
): ExtranetHotelExperience[] => {
  return experiences
    .filter((experience) => experience.hotelId !== undefined)
    .filter(
      (experience) => experience.status === ExtranetExperienceStatus.PUBLISHED
    )
    .filter((experience) =>
      experience.allowedPaxTypes.includes(PAX_TYPE.ADULT)
    );
};

// Filters experiences to only include hotel-level, published experiences that allow ADULT pax type
// and are shown in inventory (showInInventory !== false)
export const filterHotelExperiences = (
  experiences: ExtranetHotelExperience[]
): ExtranetHotelExperience[] => {
  return baseFilterHotelExperiences(experiences).filter(
    (experience) => experience.showInInventory !== false
  );
};

// Gets all hotel-level experiences including those not shown in inventory (for price calculations)
export const getHotelExperiencesIncludingHidden = (
  experiences: ExtranetHotelExperience[]
): ExtranetHotelExperience[] => {
  return baseFilterHotelExperiences(experiences);
};

// Base filter for originals: country-level, published, allow ADULT pax type
const baseFilterOriginals = (
  originals: ExtranetHotelExperience[]
): ExtranetHotelExperience[] => {
  return originals
    .filter((original) => original.countryId !== undefined)
    .filter(
      (original) => original.status === ExtranetExperienceStatus.PUBLISHED
    )
    .filter((original) => original.allowedPaxTypes.includes(PAX_TYPE.ADULT));
};

// Filters originals to only include country-level, published originals that allow ADULT pax type
// and are shown in inventory (showInInventory !== false)
export const filterOriginals = (
  originals: ExtranetHotelExperience[]
): ExtranetHotelExperience[] => {
  return baseFilterOriginals(originals).filter(
    (original) => original.showInInventory !== false
  );
};

// Gets all originals including those not shown in inventory (for price calculations)
export const getOriginalsIncludingHidden = (
  originals: ExtranetHotelExperience[]
): ExtranetHotelExperience[] => {
  return baseFilterOriginals(originals);
};

// Main function: computes packs, rooms, and experiences for a date range
export const computePacks = (
  saleEndDateString: string,
  startDateString: string,
  endDateString: string,
  packData: ExtranetHotelPack[],
  experiences: ExtranetHotelExperience[],
  originals: ExtranetHotelExperience[],
  inventoryInput: InventoryResponse,
  hasChannelManager: boolean,
  hasAutoInventory: boolean
): ComputePacksResult => {
  const filteredPacks = packData.filter((pack) =>
    isPackVisibleInExtranet(pack.status)
  );

  const endDate = new Date(endDateString);
  const dates = eachDayOfInterval({
    start: new Date(startDateString),
    end: endDate,
  });

  // Pre-compute room openings for all dates
  const computedRoomOpenings = computeRooms(
    saleEndDateString,
    startDateString,
    endDateString,
    inventoryInput,
    hasChannelManager,
    hasAutoInventory
  );

  // Visible experiences (filtered on showInInventory)
  const filteredExperiences = filterHotelExperiences(experiences);
  const filteredOriginals = filterOriginals(originals);
  const visibleExperiences = [...filteredExperiences, ...filteredOriginals];
  const visibleExperienceIds = new Set(visibleExperiences.map((exp) => exp.id));

  // All experiences including hidden ones (for price calculations)
  const allExperiencesIncludingHidden = [
    ...getHotelExperiencesIncludingHidden(experiences),
    ...getOriginalsIncludingHidden(originals),
  ];

  // Pre-compute experience openings (add one day for night stay checkout dates)
  // Use all experiences for price calculations
  const computedExperiences = computeExperiences(
    saleEndDateString,
    startDateString,
    // Add one day to the end date to include the checkout day
    formatDateWithoutHour(addDays(endDate, 1)),
    allExperiencesIncludingHidden,
    inventoryInput
  );

  // Compute packs for each date
  return dates.reduce((result, date) => {
    const dateKey = formatDateWithoutHour(date);

    const computedPacks = computePacksForDate(
      new Date(saleEndDateString),
      date,
      filteredPacks,
      inventoryInput,
      allExperiencesIncludingHidden,
      computedExperiences,
      computedRoomOpenings[dateKey] ?? {},
      visibleExperienceIds
    );

    Object.entries(computedPacks).forEach(([packId, cp]) => {
      if (cp.type === PackType.WITHOUT_ROOM) {
        return;
      }

      cp.roomsDetails.forEach((rd: PackRoomDetails) => {
        const roomOpening = computedRoomOpenings[dateKey]?.[rd.roomId];

        if (!roomOpening) {
          return;
        }

        roomOpening.packsDetails = roomOpening.packsDetails ?? [];
        roomOpening.packsDetails.push({
          packId: parseInt(packId, 10),
          totalPrice: rd.totalPrice,
          discountedTotalPrice: rd.discountedTotalPrice,
          discount: rd.discount,
        });
      });
    });

    result[dateKey] = {
      packs: computedPacks,
      rooms: computedRoomOpenings[dateKey] ?? {},
      experiences: computedExperiences[dateKey] ?? {},
    };

    return result;
  }, {} as ComputePacksResult);
};
